import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';

const REWARD_EMOJI = {
  'pre-roll': '🌀',
  '1g':  '🌱',
  '2g':  '🌿',
  '3.5g': '💚',
  '7g':  '💎',
  '14g': '👑',
};

export default function RewardsPage() {
  const { user, refreshUser } = useAuth();
  const showToast = useToast();
  const [catalog, setCatalog] = useState([]);
  const [rates, setRates] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [confirmReward, setConfirmReward] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => sessionStorage.getItem('stash_disclaimer') === '1');
  const [disclaimerClosing, setDisclaimerClosing] = useState(false);

  const acceptDisclaimer = () => {
    setDisclaimerClosing(true);
    setTimeout(() => {
      sessionStorage.setItem('stash_disclaimer', '1');
      setDisclaimerAccepted(true);
    }, 350);
  };

  useEffect(() => {
    api.get('/rewards/catalog').then(setCatalog).catch(() => {});
    api.get('/points/rates').then(setRates).catch(() => {});
  }, []);

  const handleRedeem = async (reward) => {
    setRedeeming(reward.id);
    try {
      const data = await api.post('/rewards/redeem', { rewardId: reward.id });
      showToast('success', `Redeemed! Coupon: ${data.coupon.coupon_code}`);
      await refreshUser();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setRedeeming(null);
      setConfirmReward(null);
    }
  };

  const points = user?.points || 0;
  const nextTier = catalog.find(r => r.cost > points);
  const prevTier = [...catalog].reverse().find(r => r.cost <= points);
  const progressBase = prevTier ? prevTier.cost : 0;
  const progressTarget = nextTier ? nextTier.cost : (catalog.length > 0 ? catalog[catalog.length - 1].cost : 1);
  const progressPct = nextTier ? Math.min(100, Math.round(((points - progressBase) / (progressTarget - progressBase)) * 100)) : 100;

  const pointsMap = rates?.points || rates || {};
  const spinsMap = rates?.spins || {};

  return (
    <div>
      {/* Disclaimer Modal */}
      {!disclaimerAccepted && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: 20, paddingTop: 60,
          animation: disclaimerClosing ? 'disclaimerFadeOut 0.35s ease forwards' : 'disclaimerFadeIn 0.4s ease',
        }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: '28px 24px', maxWidth: 420, width: '100%',
            textAlign: 'center',
            animation: disclaimerClosing ? 'disclaimerScaleOut 0.35s ease forwards' : 'disclaimerScaleIn 0.45s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#9888;&#65039;</div>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, color: 'var(--warning)' }}>Heads Up</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
              Point values, reward costs, and earning rates in the Stash Shop are subject to change at any time without prior notice.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}>
              Management reserves the right to adjust, modify, or remove any rewards or point balances. By continuing, you acknowledge and accept these terms.
            </p>
            <button onClick={acceptDisclaimer} className="btn-primary" style={{ width: '100%', padding: '14px', fontSize: 16, fontWeight: 700 }}>
              I Understand — Let Me Shop
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmReward && (
        <div
          className="modal-overlay"
          onClick={() => !redeeming && setConfirmReward(null)}
        >
          <div className="modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {REWARD_EMOJI[confirmReward.type] || '🎁'}
            </div>
            <h3 style={{ margin: '0 0 4px' }}>Confirm Redemption</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
              You're about to spend points on this reward
            </p>
            <div style={{
              background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
              padding: '14px 16px', marginBottom: 16, textAlign: 'left',
            }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{confirmReward.name}</div>
              <div style={{
                display: 'inline-block', background: 'rgba(236,201,75,0.15)',
                color: 'var(--warning)', borderRadius: 20, padding: '2px 10px',
                fontSize: 13, fontWeight: 700, marginBottom: 12,
              }}>
                {confirmReward.cost.toLocaleString()} pts
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Your balance</span>
                  <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{points.toLocaleString()} pts</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>After redemption</span>
                  <span style={{ fontWeight: 600 }}>{(points - confirmReward.cost).toLocaleString()} pts</span>
                </div>
              </div>
            </div>
            <div className="btn-row" style={{ flexDirection: 'column', gap: 8 }}>
              <button
                className="btn-primary"
                style={{ width: '100%', padding: '13px', fontSize: 15, fontWeight: 700 }}
                disabled={!!redeeming}
                onClick={() => handleRedeem(confirmReward)}
              >
                {redeeming ? 'Rolling up...' : 'Confirm — Cop This'}
              </button>
              <button
                className="btn-secondary"
                style={{ width: '100%' }}
                disabled={!!redeeming}
                onClick={() => setConfirmReward(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>🌿 Stash Shop</h1>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--warning)', lineHeight: 1 }}>
            {points.toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>pts</span>
        </div>
      </div>

      {/* Progress bar */}
      {nextTier && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            <span>Progress to {nextTier.name}</span>
            <span>{points.toLocaleString()} / {nextTier.cost.toLocaleString()} pts</span>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, height: 16, overflow: 'hidden', position: 'relative' }}>
            <div style={{
              width: `${progressPct}%`, height: '100%', borderRadius: 8,
              background: 'linear-gradient(90deg, var(--success), var(--accent))',
              transition: 'width 0.6s ease',
              boxShadow: '0 0 8px rgba(56,161,105,0.5)',
              position: 'relative',
            }}>
              {progressPct > 4 && (
                <span style={{
                  position: 'absolute', right: 0, top: '50%', transform: 'translate(50%, -50%)',
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#ecc94b',
                  boxShadow: '0 0 6px #ecc94b, 0 0 12px rgba(236,201,75,0.6)',
                  animation: 'badgePulse 1.2s ease-in-out infinite',
                }} />
              )}
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            🔥 {(nextTier.cost - points).toLocaleString()} pts away from {nextTier.name}
          </div>
        </div>
      )}

      {/* Earning rates */}
      {Object.keys(pointsMap).length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 10 }}>Earning Rates Per Pickup</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8 }}>
            {Object.entries(pointsMap).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).map(([grams, pts]) => {
              const spins = spinsMap[grams] || 0;
              return (
                <div key={grams} style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--warning)' }}>+{pts}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{grams}g pickup</div>
                  {spins > 0 && (
                    <div style={{ fontSize: 11, color: '#d6bcfa', marginTop: 2, fontWeight: 600 }}>
                      +{spins} spin{spins > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reward cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        {catalog.map(reward => {
          const canAfford = points >= reward.cost;
          const emoji = REWARD_EMOJI[reward.type] || '🎁';
          const ptsToGo = reward.cost - points;
          return (
            <div
              key={reward.id}
              className={canAfford ? 'card reward-unlocked' : 'card'}
              style={{
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                textAlign: 'center',
                opacity: canAfford ? 1 : 0.55,
              }}
            >
              <div>
                <div style={{ fontSize: 32, marginBottom: 8 }}>{emoji}</div>
                <h3 style={{ fontSize: 15, marginBottom: 8 }}>{reward.name}</h3>
                <div style={{
                  display: 'inline-block', marginBottom: 12,
                  background: canAfford ? 'rgba(236,201,75,0.15)' : 'rgba(74,85,104,0.3)',
                  color: canAfford ? 'var(--warning)' : 'var(--text-muted)',
                  borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 700,
                }}>
                  {reward.cost.toLocaleString()} pts
                </div>
              </div>
              {canAfford ? (
                <button
                  className="btn-primary"
                  disabled={redeeming === reward.id}
                  onClick={() => setConfirmReward(reward)}
                  style={{ marginTop: 4 }}
                >
                  {redeeming === reward.id ? 'Rolling up...' : 'Cop This'}
                </button>
              ) : (
                <div style={{
                  marginTop: 4, padding: '8px 0',
                  fontSize: 12, color: 'var(--text-muted)', fontWeight: 600,
                }}>
                  {ptsToGo.toLocaleString()} pts to go
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
